import { apiClient, unwrap } from '@/lib/apiClient';
import type {
  ActivityDetail,
  ActivityListParams,
  ActivityPublication,
  ClassActivity,
  GradeSubmissionPayload,
} from '@/types/activity';
import type { ApiSuccess } from '@/types/api';
import type { ActivityPayload } from '../activities/schema';

/**
 * Every call the class-activities screens make.
 *
 * As with the rest of the institute area there is no institute id in any path — the
 * server takes it from the caller's token, so there is nothing for a client to
 * point at another institute's work. The academic year is the server's decision
 * too, and which activities come back is scoped to the caller's own allotments.
 */

/**
 * Activities travel as multipart because the guidelines sheet travels with
 * them: an activity half-saved — set, but with its rubric lost — is worse than
 * one that failed outright. The record itself goes in a JSON text field beside
 * the file, which is the same shape the admission form uses.
 */
function toFormData(
  payload: Partial<ActivityPayload> & { publication?: ActivityPublication; removeGuideline?: true },
  guideline?: File | null,
): FormData {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  if (guideline) form.append('guideline', guideline);
  return form;
}

/**
 * Hands the Content-Type back to the browser for a multipart request.
 *
 * `apiClient` sets `application/json` for every call, which is right for all but
 * these two: with it in place the boundary is never written, the server reads a
 * multipart body as JSON and the whole request fails before it reaches a
 * handler. Setting it to `undefined` makes axios leave the header off so the
 * browser can write `multipart/form-data; boundary=…` itself.
 */
const MULTIPART = { headers: { 'Content-Type': undefined } };

export const activitiesApi = {
  list: (params: ActivityListParams = {}) =>
    unwrap(
      apiClient.get<ApiSuccess<ClassActivity[]>>('/institute/activities', {
        // Empty filters are dropped rather than sent as blanks — the server
        // validates the shape of what arrives, and `status=` is not a status.
        params: {
          ...(params.classSectionUuid && { classSectionUuid: params.classSectionUuid }),
          ...(params.subjectUuid && { subjectUuid: params.subjectUuid }),
          ...(params.activityType && { activityType: params.activityType }),
          ...(params.dueDate && { dueDate: params.dueDate }),
          ...(params.status && { status: params.status }),
          ...(params.withdrawn && { withdrawn: 'true' }),
        },
      }),
    ),

  /** The activity and its roster in one call — the review screen's only read. */
  get: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<ActivityDetail>>(`/institute/activities/${uuid}`)),

  /** `DRAFT` keeps it off the families' lists; omitted means published straight away. */
  create: (payload: ActivityPayload & { publication?: ActivityPublication }, guideline?: File | null) =>
    unwrap(
      apiClient.post<ApiSuccess<ClassActivity>>(
        '/institute/activities',
        toFormData(payload, guideline),
        MULTIPART,
      ),
    ),

  /** `publication: 'PUBLISHED'` releases a draft — one way only; a live activity is withdrawn, not un-published. */
  update: (
    uuid: string,
    payload: Partial<ActivityPayload> & { publication?: 'PUBLISHED'; removeGuideline?: true },
    guideline?: File | null,
  ) =>
    unwrap(
      apiClient.patch<ApiSuccess<ClassActivity>>(
        `/institute/activities/${uuid}`,
        toFormData(payload, guideline),
        MULTIPART,
      ),
    ),

  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/activities/${uuid}`)),

  /**
   * Records one child's work. Idempotent — sending it twice corrects the first
   * entry rather than adding a second, which is why it is a PUT.
   */
  grade: (uuid: string, studentUuid: string, payload: GradeSubmissionPayload) =>
    unwrap(
      apiClient.put<ApiSuccess<{ studentUuid: string; status: string; scoreObtained: number | null }>>(
        `/institute/activities/${uuid}/submissions/${studentUuid}`,
        payload,
      ),
    ),

  /**
   * Where the guidelines sheet is served from.
   *
   * A path rather than a fetch: it is handed to the shared authenticated
   * download helper, because the file sits behind the same bearer token as
   * every other read and is never a public URL (CLAUDE.md §11).
   */
  guidelinePath: (uuid: string) => `/institute/activities/${uuid}/guideline`,

  /** The same, for what one child handed in — read behind the same token. */
  submissionFilePath: (uuid: string, studentUuid: string) =>
    `/institute/activities/${uuid}/submissions/${studentUuid}/attachment`,
};
